import { PageTitle } from "@/components/molecules";
import {
  EditUserForm,
  EditUserFormBizsol,
  EditUserFormConnectBoard,
  EditUserFormValues,
} from "@/components/organisms";
import { MainTemplate } from "@/components/templates";
import {
  Authority,
  BizsolState,
  useCancelWaitingForConnectToBizsolMutation,
  useChangeBizsolStateToWaitingMutation,
  useChangeToMemberMutation,
  useDeleteCompanyUserMutation,
  useDisconnectFromBizsolMutation,
  useEditCompanyUserMutation,
  useGetUserLazyQuery,
  User as graphqlUser,
} from "@/generated/graphql";
import {
  useCompanyLazyQuery,
  useDeleteAdminMutation,
} from "@/generated/graphql_billing";
import { isConnectedToBizsol, User } from "@/models";
import { FormProvider, RegisterOptions, useForm } from "react-hook-form";
import {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import Router from "next/router";
import { passwordValidation } from "@/components/validations";
import { useSnackBar } from "@moneyforward/mfx_biz_react_components";
import { FrontendErrorMsgs, moveToErrorPage } from "@/libs/errors";
import config from "@/libs/config";
import { AuthContext, AuthStatus } from "@/contexts/AuthContext";
import { useCallBillingDeleteAdminMutation } from "@/components/hooks/useCallBillingDeleteAdminMutation";
import { useSetRecoilState } from "recoil";
import {
  BizsolDisconnectModalState,
  bizsolDisconnectModalState,
} from "@/store/bizsolDisconnectModalState";
import { ChangeMemberConfirmModal } from "../molecules/ChangeMemberConfirmModal";
import useRollbarUtil from "../hooks/useRollbarUtil";
import { BizsolDeleteUserAction, canTargetUserBeDeleted } from "@/libs/bizsol";

type Props = {
  sub: string;
};

export const EditUserPage = ({ sub }: Props) => {
  const { rollbar, getNotifyErrorHandlerForGraphQL } = useRollbarUtil();
  const { authStatus, userInfo } = useContext(AuthContext);
  const [user, setUser] = useState(new User({} as graphqlUser));
  const [bizsolState, setBizsolState] = useState<BizsolState>();
  const [passwordRegisterOptions, setPasswordRegisterOptions] =
    useState<RegisterOptions>();
  const setDisconnectState = useSetRecoilState(bizsolDisconnectModalState);

  const { notify } = useSnackBar();

  const isOwnUserProfile = userInfo?.sub === sub; // ログインユーザー自身のユーザー編集画面かどうか
  const usingBizsol: boolean = config.USING_BIZSOL;
  const usingConnectBoard: boolean = config.USING_CONNBOARD;
  const [visibleChangeMemberConfirmModal, setVisibleChangeMemberConfirmModal] =
    useState<boolean>(false);

  const [getUserQuery, { loading: getLoading }] = useGetUserLazyQuery({
    variables: { sub: sub },
    onCompleted: (data) => {
      setUser(data.user as User);
      setBizsolState(data.user.bizsolState);
    },
    onError: getNotifyErrorHandlerForGraphQL("Failed to get user info"),
  });

  useEffect(() => {
    if (authStatus === AuthStatus.SignedIn) {
      getUserQuery();
    }
  }, [authStatus]);

  const methods = useForm<EditUserFormValues>({
    reValidateMode: "onBlur",
    defaultValues: {
      firstName: user.firstName,
      firstNameKana: user.firstNameKana,
      middleName: user.middleName,
      middleNameKana: user.middleNameKana,
      lastName: user.lastName,
      lastNameKana: user.lastNameKana,
    },
  });

  useEffect(() => {
    methods.reset(user);
    methods.setValue("password", "");
  }, [user]);

  // =============== Mutations ===============
  const [editCompanyUserMutation, { loading: editLoading }] =
    useEditCompanyUserMutation({
      onCompleted: (data) => {
        setUser(data.editCompanyUser.user as User);
        notify("変更が完了しました");
      },
      onError: getNotifyErrorHandlerForGraphQL(
        "Failed to edit user",
        "変更に失敗しました"
      ),
    });

  const handleSubmit = async (formValues: EditUserFormValues) => {
    await editCompanyUserMutation({
      variables: {
        input: {
          sub: user.sub,
          user: {
            password: formValues.password,
            lastName: formValues.lastName,
            lastNameKana: formValues.lastNameKana,
            middleName: formValues.middleName,
            middleNameKana: formValues.middleNameKana,
            firstName: formValues.firstName,
            firstNameKana: formValues.firstNameKana,
          },
        },
      },
    });
  };

  const [changeToMemberMutation, { loading: changeToMemberLoading }] =
    useChangeToMemberMutation({
      variables: { input: { sub: user.sub } },
      onCompleted: async (data) => {
        if (data.changeToMember.result) {
          notify("権限を一般に変更しました");
          await getUserQuery(); // call query again and set updated user
          setVisibleChangeMemberConfirmModal(false);
        }
      },
      onError: getNotifyErrorHandlerForGraphQL(
        "Failed to change to member",
        "権限の変更に失敗しました"
      ),
    });

  const [
    changeBizsolStateToWaitingMutation,
    { loading: changeBizsolStateToWaitingLoading },
  ] = useChangeBizsolStateToWaitingMutation({
    variables: { input: { sub: user.sub } },
    onCompleted: (data) => {
      if (data.changeBizsolStateToWaiting.result) {
        setBizsolState(BizsolState.Waiting);
        notify(`${config.BIZSOL_DISPLAY_NAME}との連携待ち状態に変更しました`);
      }
    },
    onError: getNotifyErrorHandlerForGraphQL(
      "Failed to change Bizsol state to waiting",
      `${config.BIZSOL_DISPLAY_NAME}との連携に失敗しました`
    ),
  });

  const [
    cancelWaitingForConnectToBizsolMutation,
    { loading: cancelWaitingForConnectBizsolLoading },
  ] = useCancelWaitingForConnectToBizsolMutation({
    variables: { input: { sub: user.sub } },
    onCompleted: (data) => {
      if (data.cancelWaitingForConnectToBizsol.result) {
        setBizsolState(BizsolState[""]);
        notify(`${config.BIZSOL_DISPLAY_NAME}との連携待ち状態を解除しました`);
      }
    },
    onError: getNotifyErrorHandlerForGraphQL(
      "Failed to cancel waiting for connect to Bizsol",
      `${config.BIZSOL_DISPLAY_NAME}との連携待ち状態の解除に失敗しました`
    ),
  });

  const [deleteCompanyUserMutation, { loading: deleteLoading }] =
    useDeleteCompanyUserMutation({
      variables: { input: { sub: user.sub } },
      onCompleted: (data) => {
        if (data.deleteCompanyUser.result) {
          notify("削除が完了しました"); // FIXME: 文言を調整する
          Router.push("/manage-users"); // FIXME: history.back() の方が良い？
        } else {
          notify("削除に失敗しました"); // FIXME: 文言を調整する
        }
      },
      onError: getNotifyErrorHandlerForGraphQL(
        "Failed to delete user",
        "削除に失敗しました"
      ),
    });

  const [disconnectFromBizsolMutation, { loading: disconnectLoading }] =
    useDisconnectFromBizsolMutation({
      variables: { input: { sub: user.sub } },
      onCompleted: (data) => {
        if (data.disconnectFromBizsol.result) {
          setBizsolState(BizsolState[""]);
          notify(
            `${config.BIZSOL_DISPLAY_NAME}との連携を解除しました。\n再連携に進みます。`
          );
          Router.push("/bizsol/redirect-login");
        }
      },
      onError: getNotifyErrorHandlerForGraphQL(
        "Failed to disconnect from Bizsol",
        `${config.BIZSOL_DISPLAY_NAME}との連携解除に失敗しました`
      ),
    });

  const [disconnectFromBizsolForDisconnectModalMutation] =
    useDisconnectFromBizsolMutation({
      variables: { input: { sub: user.sub } },
    });
  const [callBillingDeleteAdminMutation] = useCallBillingDeleteAdminMutation();

  // =============== Handlers ===============
  const onClickChangeToAdmin = () => {
    Router.push("/apply-for-change-to-admin/" + sub);
  };

  const onClickChangeEmailAddress = () => {
    Router.push("/request-change-email-address/" + sub);
  };

  const onInputPassword = (event: ChangeEvent<HTMLInputElement>) => {
    const password = event.target.value;

    if (password === "") {
      setPasswordRegisterOptions({
        validate: {},
      });
    } else {
      setPasswordRegisterOptions({
        validate: passwordValidation,
      });
    }
  };

  const [deleteAdmin, { loading: deleteAdminLoading }] = useDeleteAdminMutation(
    {
      onCompleted: async (data) => {
        if (data.deleteAdmin.result) {
          await companyQuery();
          notify("契約管理者を削除しました");
        }
      },
      onError: getNotifyErrorHandlerForGraphQL("Failed to delete admin user"),
      context: {
        api: "billing",
      },
    }
  );

  // Bizso再連携ボタンハンドラー
  // 自身のbizsol連携を解除し、連携待ち状態にする
  // 加えて、所属する事業者内のすべての他ユーザーのBizsol連携を解除する
  const onClickBizsolReconnect = async () => {
    // 自身と所属する事業者内のすべての他ユーザーのBizsol連携を解除する(mutation)
    disconnectFromBizsolMutation();
  };

  // Bizsol連携解除ボタンハンドラー
  const onClickBizsolDisconnect = (
    disconnectState: BizsolDisconnectModalState
  ) => {
    disconnectFromBizsolForDisconnectModalMutation()
      .then(async (data) => {
        if (data.data?.disconnectFromBizsol.result) {
          setBizsolState(BizsolState.Waiting);
          setUser({
            ...user,
            authority: Authority.Member,
          } as User);

          if (userIsAdminUsersAtMikatanoID) {
            const result = await callBillingDeleteAdminMutation(sub);
            if (result.error) {
              moveToErrorPage();
            } else {
              setUserIsAdminUsersAtMikatanoID(false);
              setDisconnectState(disconnectState);
            }
          } else {
            setDisconnectState(disconnectState);
          }
        } else {
          moveToErrorPage();
        }
      })
      .catch((err) => {
        rollbar.error(
          "Failed to disconnect from Bizsol for disconnect modal",
          err
        );
        moveToErrorPage();
      });
  };

  const submitting =
    editLoading ||
    deleteLoading ||
    changeToMemberLoading ||
    changeBizsolStateToWaitingLoading ||
    cancelWaitingForConnectBizsolLoading ||
    disconnectLoading ||
    deleteAdminLoading;

  const userSub = userInfo.sub;
  const companyId = userInfo.xCompanyID;
  const [meIsAdminUsersAtMikatanoID, setMeIsAdminUsersAtMikatanoID] =
    useState<boolean>(false);
  const [userIsAdminUsersAtMikatanoID, setUserIsAdminUsersAtMikatanoID] =
    useState<boolean>(false);
  const [completedCompanyQuery, setCompletedCompanyQuery] =
    useState<boolean>(false);
  const [companyQuery] = useCompanyLazyQuery({
    onCompleted: (data) => {
      if (data.company.admins) {
        if (data.company.admins.find((admin) => admin.userSub === userSub)) {
          setMeIsAdminUsersAtMikatanoID(true);
        }

        if (data.company.admins.find((admin) => admin.userSub === sub)) {
          setUserIsAdminUsersAtMikatanoID(true);
        }
      }
    },
    onError: getNotifyErrorHandlerForGraphQL(
      "Failed to get company information"
    ),
    context: {
      api: "billing",
    },
    variables: {
      uuid: companyId as never,
    },
  });

  const deleteCompanyUser = useCallback(
    (selectedUserSub: string, isSelectedUserContractAdmin: boolean) => {
      (async (sub: string) => {
        // deleteAdmin if the selected user is ContractAdmin
        if (isSelectedUserContractAdmin) {
          await deleteAdmin({
            variables: {
              input: { userSub: sub },
            },
          });
        }
        await deleteCompanyUserMutation();
      })(selectedUserSub);
    },
    [deleteAdmin, deleteCompanyUserMutation]
  );

  const onClickDelete = useCallback(() => {
    if (isOwnUserProfile) {
      notify("自分自身のIDは削除できません。");
      return;
    }
    if (userIsAdminUsersAtMikatanoID) {
      notify(FrontendErrorMsgs.CONTRACT_ADMIN_CANNOT_BE_DELETED);
      return;
    }

    if (user.authority === Authority.Admin) {
      notify(FrontendErrorMsgs.ADMIN_CANNOT_BE_DELETED);
      return;
    }

    deleteCompanyUser(user.sub, userIsAdminUsersAtMikatanoID);
  }, [
    user.sub,
    user.authority,
    userIsAdminUsersAtMikatanoID,
    isOwnUserProfile,
  ]);

  // bizsol連携済みの管理者を削除可能にするため、管理者の場合は一般に変更した後削除する
  // bizsolフォーム用の削除ボタンハンドラー
  const onClickDeleteBizsol = useCallback(() => {
    const action = canTargetUserBeDeleted({
      isTargetLoggedInUser: isOwnUserProfile,
      bankCode: config.BANK_CODE,
      isTargetConnectedToBizsol: isConnectedToBizsol(bizsolState),
      targetAuthority: user.authority,
      isTargetContractAdmin: userIsAdminUsersAtMikatanoID,
    });

    switch (action) {
      case BizsolDeleteUserAction.Delete:
        deleteCompanyUser(user.sub, userIsAdminUsersAtMikatanoID);
        break;
      case BizsolDeleteUserAction.ChangeToMemberAndDelete:
        changeToMemberMutation({
          onCompleted: (data) => {
            if (data.changeToMember.result) {
              deleteCompanyUser(user.sub, userIsAdminUsersAtMikatanoID);
              return;
            }
            notify("削除に失敗しました");
          },
          onError: getNotifyErrorHandlerForGraphQL(
            "Failed to change to member",
            "削除に失敗しました"
          ),
        });
        break;
      case BizsolDeleteUserAction.CannotDeleteAdmin:
        notify(FrontendErrorMsgs.ADMIN_CANNOT_BE_DELETED);
        break;
      case BizsolDeleteUserAction.CannotDeleteContractAdmin:
        notify(FrontendErrorMsgs.CONTRACT_ADMIN_CANNOT_BE_DELETED);
        break;
      case BizsolDeleteUserAction.CannotDeleteOwnself:
        notify("自分自身のIDは削除できません。");
        break;
      default:
        rollbar.error(`unexpected BizsolDeleteUserAction: ${action}`);
    }
  }, [
    user.sub,
    user.authority,
    userIsAdminUsersAtMikatanoID,
    isOwnUserProfile,
  ]);

  useEffect(() => {
    (async () => {
      await companyQuery().finally(() => setCompletedCompanyQuery(true));
    })();
  }, [userInfo, sub]);

  // =============== Conditional rendering ===============
  let editUserForm;
  if (usingBizsol) {
    editUserForm = (
      <EditUserFormBizsol
        username={user.username}
        authority={user.authority}
        email={user.email}
        sub={sub}
        waitingForChangeToAdmin={user.waitingForChangeToAdmin}
        bizsolState={bizsolState}
        bizsolLoginID={user.bizsolLoginID}
        isOwnUserProfile={isOwnUserProfile}
        onSubmit={handleSubmit}
        onClickChangeToMember={() => setVisibleChangeMemberConfirmModal(true)}
        onClickChangeToAdmin={onClickChangeToAdmin}
        onClickDelete={onClickDeleteBizsol}
        onClickChangeEmailAddress={onClickChangeEmailAddress}
        onClickResendEmail={onClickChangeToAdmin}
        onClickChangeToWaiting={changeBizsolStateToWaitingMutation}
        onClickCancelWaiting={cancelWaitingForConnectToBizsolMutation}
        onClickReconnect={onClickBizsolReconnect}
        onClickDisconnect={onClickBizsolDisconnect}
        submitting={submitting}
        passwordRegisterOptions={passwordRegisterOptions}
        onInputPassword={onInputPassword}
        meIsAdminUsersAtMikatanoID={meIsAdminUsersAtMikatanoID}
        userIsAdminUsersAtMikatanoID={userIsAdminUsersAtMikatanoID}
      />
    );
  } else if (usingConnectBoard) {
    editUserForm = (
      <EditUserFormConnectBoard
        username={user.username}
        authority={user.authority}
        email={user.email}
        waitingForChangeToAdmin={user.waitingForChangeToAdmin}
        connectBoardState={user.connectBoardState}
        connectBoardLoginID={user.connectBoardLoginID}
        isOwnUserProfile={isOwnUserProfile}
        onSubmit={handleSubmit}
        onClickChangeToMember={() => setVisibleChangeMemberConfirmModal(true)}
        onClickChangeToAdmin={onClickChangeToAdmin}
        onClickDelete={onClickDelete}
        onClickChangeEmailAddress={onClickChangeEmailAddress}
        onClickResendEmail={onClickChangeToAdmin}
        submitting={submitting}
        passwordRegisterOptions={passwordRegisterOptions}
        onInputPassword={onInputPassword}
        meIsAdminUsersAtMikatanoID={meIsAdminUsersAtMikatanoID}
        userIsAdminUsersAtMikatanoID={userIsAdminUsersAtMikatanoID}
      />
    );
  } else {
    editUserForm = completedCompanyQuery && (
      <EditUserForm
        isOwnUserProfile={isOwnUserProfile}
        username={user.username}
        authority={user.authority}
        email={user.email}
        waitingForChangeToAdmin={user.waitingForChangeToAdmin}
        onSubmit={handleSubmit}
        onClickChangeToMember={() => setVisibleChangeMemberConfirmModal(true)}
        onClickChangeToAdmin={onClickChangeToAdmin}
        onClickDelete={onClickDelete}
        onClickChangeEmailAddress={onClickChangeEmailAddress}
        onClickResendEmail={onClickChangeToAdmin}
        submitting={editLoading}
        passwordRegisterOptions={passwordRegisterOptions}
        onInputPassword={onInputPassword}
        meIsAdminUsersAtMikatanoID={meIsAdminUsersAtMikatanoID}
        userIsAdminUsersAtMikatanoID={userIsAdminUsersAtMikatanoID}
      />
    );
  }

  // =============== Render ===============
  return (
    <MainTemplate title={`利用者情報の編集`}>
      <PageTitle mainTitle={`利用者情報の編集`} />
      {getLoading ? (
        <p>loading...</p>
      ) : (
        <>
          <FormProvider {...methods}>
            <div>{editUserForm}</div>
            {visibleChangeMemberConfirmModal && (
              <ChangeMemberConfirmModal
                loading={changeToMemberLoading}
                onClickCancel={() => setVisibleChangeMemberConfirmModal(false)}
                onClickChange={changeToMemberMutation}
              />
            )}
          </FormProvider>
        </>
      )}
    </MainTemplate>
  );
};
